import { useMutation, useQueryClient } from "@tanstack/react-query";
import { KeyRoundIcon } from "lucide-react";
import { Link } from "react-router";
import { toast } from "sonner";
import type { ModelAccessView } from "../../../../shared/api.ts";
import { Button } from "@/components/ui/button.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { api, queryKeys } from "@/lib/api.ts";
import { errorMessage } from "@/lib/hooks.ts";

/**
 * Shown while the tenant has no model access: what to do, a way into Settings, and a request to the administrators,
 * which Admin → Clients shows and the administrators get by email when mail is set up.
 */
export function ModelAccessNotice({ view, settingsLink = true }: { view: ModelAccessView; settingsLink?: boolean }) {
  const { t, fmt } = useI18n();
  const queryClient = useQueryClient();
  const ask = useMutation({
    mutationFn: () => api.requestModelAccess(),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKeys.modelAccess, next);
      toast.success(t.access.sent);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });
  return (
    <div role="status" className="flex gap-3 rounded-xl border border-warning-border bg-warning-soft px-4 py-3 text-[13px]">
      <KeyRoundIcon className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-warning">{t.access.title}</p>
        <p className="mt-1 text-foreground/80">{view.shared.enabled && !view.shared.available ? t.access.sharedDown : t.access.text}</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {settingsLink ? (
            <Button asChild size="sm" variant="outline">
              <Link to="/app/settings">{t.access.openSettings}</Link>
            </Button>
          ) : null}
          <Button size="sm" onClick={() => ask.mutate()} disabled={ask.isPending}>
            {view.accessRequestedAt ? t.access.askAgain : t.access.ask}
          </Button>
          {view.accessRequestedAt ? <span className="text-[12px] text-muted-foreground">{t.access.asked(fmt.dateTime(view.accessRequestedAt))}</span> : null}
        </div>
      </div>
    </div>
  );
}
